const directory = {
  en: {
    title: 'Mentor directory',
    subtitle: 'Find and explore mentors across various subjects',
    searchPlaceholder: 'Search mentors by name...',
    filterLabel: 'Filter by Subject:',
    subjects: {
      all: 'All', mathematics: 'Mathematics', english: 'English', science: 'Science',
      history: 'History', languages: 'Languages', technology: 'Technology', arts: 'Arts', other: 'Other',
    },
    loading: 'Loading mentors...',
    emptyTitle: 'No mentors found',
    emptyDescription: 'Try a different keyword, or clear the filters to see more results.',
    errorFailedToLoad: 'Failed to load mentors',
    pagination: {
      previous: 'Previous', next: 'Next', pageInfo: 'Page {page} of {totalPages}',
    },
    studentDetail: {
      defaultStudentName: 'Student',
      backButton: 'Back to Student Status',
      studentIdLabel: 'Student ID: {studentId}',
      pageSubtitle: 'Risk assessment, recommendations and interventions',
      tabs: { progress: 'Progress', assessment: 'Assessment and Recommendations' },
      progressPlaceholder: 'Progress data for this student will appear here.',
      progressNote: '(The existing teacher progress view is at /teacher/students/:studentId. Merge or link from here in a future story as needed.)',
      assessment: {
        sectionTitle: 'Assessment',
        empty: "No assessment available yet. One will be generated on Monday or after the student's next activity.",
        assessedLabel: 'Assessed: {time}',
        triggerLabel: 'Trigger: {trigger}',
        riskFactorsLabel: 'Risk factors:',
        narrativeTitle: 'Assessment narrative',
        interventionTitle: 'Suggested intervention',
      },
      riskBadge: {
        title: 'Risk score: {score} — {band}',
        noData: 'No data',
      },
      recommendations: {
        sectionTitle: 'Recommendations',
        addButton: 'Add recommendation',
        empty: 'No active recommendations.',
        kebabAria: 'More actions',
        suppressAction: 'Suppress recommendation',
        sourceBadgeStarterPlan: 'Starter plan',
        mentorFrom: 'From {name}',
        targetTypeFallback: 'Target',
        targetUnavailable: 'This content is no longer available',
      },
      targetTypes: { module: 'Module', lesson: 'Lesson', quiz: 'Quiz' },
      states: {
        pending: 'Pending', seen: 'Seen', accepted: 'Accepted', dismissed: 'Dismissed',
        completed: 'Completed', mentorSuppressed: 'Suppressed', superseded: 'Superseded', expired: 'Expired',
      },
      suppressed: {
        toggleShow: 'Show suppressed and history ({count})',
        toggleHide: 'Hide suppressed and history ({count})',
      },
      interventionLog: { title: 'Intervention log', empty: 'No interventions recorded yet.' },
      suppressModal: {
        title: 'Suppress this recommendation',
        body: 'This recommendation will be hidden from the student. The reason you give helps the AI generate better suggestions next time.',
        presets: {
          prerequisiteNotMet: 'Prerequisite not met',
          notRelevantNow: 'Not relevant right now',
          studentNotReady: 'Student not ready for this',
          other: 'Other',
        },
        otherPlaceholder: 'Describe why this recommendation is not appropriate',
        otherAria: 'Suppression reason',
        cancel: 'Cancel',
        suppress: 'Suppress',
        suppressing: 'Suppressing...',
      },
      addRecModal: {
        titleFor: 'Add recommendation for {name}',
        targetTypeFieldLabel: 'Target type',
        targetFieldLabel: 'Target',
        loadingContent: 'Loading content...',
        contentErrorNote: "Could not load this student's courses. Close the dialog and try again.",
        noItemsAvailable: { module: 'No modules available', lesson: 'No lessons available', quiz: 'No quizzes available' },
        selectItem: { module: 'Select a module...', lesson: 'Select a lesson...', quiz: 'Select a quiz...' },
        reasoningLabel: 'Reasoning',
        reasoningPlaceholder: 'Explain why this recommendation is appropriate for this student.',
        priorityLabel: 'Priority',
        skillsLabel: 'Skills addressed',
        skillsSearchLoading: 'Loading skills...',
        skillsSearchPlaceholder: 'Search skills...',
        skillsSearchAria: 'Search skills',
        noMatchingSkills: 'No matching skills',
        removeSkillAria: 'Remove skill {name}',
        cancel: 'Cancel',
        add: 'Add recommendation',
        adding: 'Adding...',
      },
      retry: 'Try again',
      time: {
        today: 'Today', yesterday: 'Yesterday',
        daysAgo: '{count} days ago',
        weekAgo: '1 week ago', weeksAgo: '{count} weeks ago',
        monthAgo: '1 month ago', monthsAgo: '{count} months ago',
      },
      toasts: {
        suppressed: 'Suppressed',
        suppressError: 'Unable to suppress recommendation. Please try again.',
        recommendationAdded: 'Recommendation added',
      },
      errors: {
        assessmentLoad: 'Unable to load assessment. Please try again.',
        historyLoad: 'Unable to load risk history.',
        targetNotAvailable: 'This target is not available for this student. Please select a different module, lesson, or quiz.',
        skillsUnavailable: 'One or more selected skills are not available. Please refresh and try again.',
        networkError: 'Unable to connect. Please try again.',
        addRecGeneric: 'Unable to add recommendation. Please check your inputs and try again.',
      },
      tabsAria: 'Student sections',
      openProfile: 'Open student profile',
    },
  },
  ru: {
    title: 'Каталог менторов',
    subtitle: 'Найдите и изучите менторов по различным предметам',
    searchPlaceholder: 'Поиск менторов по имени...',
    filterLabel: 'Фильтр по предмету:',
    subjects: {
      all: 'Все', mathematics: 'Математика', english: 'Английский', science: 'Наука',
      history: 'История', languages: 'Языки', technology: 'Технологии', arts: 'Искусство', other: 'Другое',
    },
    loading: 'Загрузка менторов...',
    emptyTitle: 'Менторы не найдены',
    emptyDescription: 'Попробуйте другой запрос или сбросьте фильтры, чтобы увидеть больше результатов.',
    errorFailedToLoad: 'Не удалось загрузить менторов',
    pagination: {
      previous: 'Назад', next: 'Вперёд', pageInfo: 'Страница {page} из {totalPages}',
    },
    studentDetail: {
      defaultStudentName: 'Ученик',
      backButton: 'Назад к статусу учеников',
      studentIdLabel: 'ID ученика: {studentId}',
      pageSubtitle: 'Оценка риска, рекомендации и вмешательства',
      tabs: { progress: 'Прогресс', assessment: 'Оценка и рекомендации' },
      progressPlaceholder: 'Здесь будут отображаться данные о прогрессе этого ученика.',
      progressNote: '(Существующий вид прогресса для учителя находится по адресу /teacher/students/:studentId. При необходимости объедините его или добавьте ссылку в будущей задаче.)',
      assessment: {
        sectionTitle: 'Оценка',
        empty: 'Оценка пока недоступна. Она будет сформирована в понедельник или после следующей активности ученика.',
        assessedLabel: 'Оценено: {time}',
        triggerLabel: 'Триггер: {trigger}',
        riskFactorsLabel: 'Факторы риска:',
        narrativeTitle: 'Описание оценки',
        interventionTitle: 'Рекомендуемое вмешательство',
      },
      riskBadge: {
        title: 'Оценка риска: {score} — {band}',
        noData: 'Нет данных',
      },
      recommendations: {
        sectionTitle: 'Рекомендации',
        addButton: 'Добавить рекомендацию',
        empty: 'Нет активных рекомендаций.',
        kebabAria: 'Дополнительные действия',
        suppressAction: 'Скрыть рекомендацию',
        sourceBadgeStarterPlan: 'Стартовый план',
        mentorFrom: 'От {name}',
        targetTypeFallback: 'Цель',
        targetUnavailable: 'Этот материал больше недоступен',
      },
      targetTypes: { module: 'Модуль', lesson: 'Урок', quiz: 'Тест' },
      states: {
        pending: 'В ожидании', seen: 'Просмотрено', accepted: 'Принято', dismissed: 'Отклонено',
        completed: 'Завершено', mentorSuppressed: 'Скрыто', superseded: 'Заменено', expired: 'Истекло',
      },
      suppressed: {
        toggleShow: 'Показать скрытые и историю ({count})',
        toggleHide: 'Скрыть скрытые и историю ({count})',
      },
      interventionLog: { title: 'Журнал вмешательств', empty: 'Вмешательств пока не зафиксировано.' },
      suppressModal: {
        title: 'Скрыть эту рекомендацию',
        body: 'Эта рекомендация будет скрыта от ученика. Указанная причина поможет ИИ давать более точные рекомендации в будущем.',
        presets: {
          prerequisiteNotMet: 'Не выполнены предварительные условия',
          notRelevantNow: 'Сейчас неактуально',
          studentNotReady: 'Ученик пока не готов к этому',
          other: 'Другое',
        },
        otherPlaceholder: 'Опишите, почему эта рекомендация неуместна',
        otherAria: 'Причина скрытия',
        cancel: 'Отмена',
        suppress: 'Скрыть',
        suppressing: 'Скрытие...',
      },
      addRecModal: {
        titleFor: 'Добавить рекомендацию для {name}',
        targetTypeFieldLabel: 'Тип цели',
        targetFieldLabel: 'Цель',
        loadingContent: 'Загрузка содержимого...',
        contentErrorNote: 'Не удалось загрузить курсы ученика. Закройте окно и попробуйте ещё раз.',
        noItemsAvailable: { module: 'Нет доступных модулей', lesson: 'Нет доступных уроков', quiz: 'Нет доступных тестов' },
        selectItem: { module: 'Выберите модуль...', lesson: 'Выберите урок...', quiz: 'Выберите тест...' },
        reasoningLabel: 'Обоснование',
        reasoningPlaceholder: 'Объясните, почему эта рекомендация подходит этому ученику.',
        priorityLabel: 'Приоритет',
        skillsLabel: 'Затрагиваемые навыки',
        skillsSearchLoading: 'Загрузка навыков...',
        skillsSearchPlaceholder: 'Поиск навыков...',
        skillsSearchAria: 'Поиск навыков',
        noMatchingSkills: 'Совпадений не найдено',
        removeSkillAria: 'Удалить навык {name}',
        cancel: 'Отмена',
        add: 'Добавить рекомендацию',
        adding: 'Добавление...',
      },
      retry: 'Повторить',
      time: {
        today: 'Сегодня', yesterday: 'Вчера',
        daysAgo: '{count} дн. назад',
        weekAgo: '1 неделю назад', weeksAgo: '{count} нед. назад',
        monthAgo: '1 месяц назад', monthsAgo: '{count} мес. назад',
      },
      toasts: {
        suppressed: 'Скрыто',
        suppressError: 'Не удалось скрыть рекомендацию. Попробуйте снова.',
        recommendationAdded: 'Рекомендация добавлена',
      },
      errors: {
        assessmentLoad: 'Не удалось загрузить оценку. Попробуйте снова.',
        historyLoad: 'Не удалось загрузить историю риска.',
        targetNotAvailable: 'Эта цель недоступна для этого ученика. Пожалуйста, выберите другой модуль, урок или тест.',
        skillsUnavailable: 'Один или несколько выбранных навыков недоступны. Обновите страницу и попробуйте снова.',
        networkError: 'Не удалось подключиться. Попробуйте снова.',
        addRecGeneric: 'Не удалось добавить рекомендацию. Проверьте введённые данные и попробуйте снова.',
      },
      tabsAria: 'Разделы студента',
      openProfile: 'Открыть профиль студента',
    },
  },
  uz: {
    title: 'Mentorlar katalogi',
    subtitle: "Turli fanlar bo'yicha mentorlarni toping va ko'rib chiqing",
    searchPlaceholder: "Mentorlarni ism bo'yicha qidirish...",
    filterLabel: "Fan bo'yicha filtrlash:",
    subjects: {
      all: 'Barchasi', mathematics: 'Matematika', english: 'Ingliz tili', science: 'Fan',
      history: 'Tarix', languages: 'Tillar', technology: 'Texnologiya', arts: "San'at", other: 'Boshqa',
    },
    loading: 'Mentorlar yuklanmoqda...',
    emptyTitle: 'Mentorlar topilmadi',
    emptyDescription: "Boshqa kalit so'z bilan qidirib ko'ring yoki ko'proq natija ko'rish uchun filtrlarni tozalang.",
    errorFailedToLoad: "Mentorlarni yuklab bo'lmadi",
    pagination: {
      previous: 'Oldingi', next: 'Keyingi', pageInfo: '{totalPages} dan {page}-sahifa',
    },
    studentDetail: {
      defaultStudentName: "O'quvchi",
      backButton: "O'quvchi holatiga qaytish",
      studentIdLabel: "O'quvchi ID: {studentId}",
      pageSubtitle: "Xavf bahosi, tavsiyalar va aralashuvlar",
      tabs: { progress: 'Progress', assessment: 'Baholash va tavsiyalar' },
      progressPlaceholder: "Bu o'quvchining progress ma'lumotlari shu yerda ko'rsatiladi.",
      progressNote: "(O'qituvchi uchun mavjud progress sahifasi /teacher/students/:studentId manzilida joylashgan. Zarur bo'lsa, kelajakdagi vazifada bu yerga birlashtiring yoki havola qo'shing.)",
      assessment: {
        sectionTitle: 'Baholash',
        empty: "Hozircha baholash mavjud emas. U dushanba kuni yoki o'quvchining keyingi faoliyatidan so'ng yaratiladi.",
        assessedLabel: 'Baholangan: {time}',
        triggerLabel: 'Sabab: {trigger}',
        riskFactorsLabel: 'Xavf omillari:',
        narrativeTitle: 'Baholash tavsifi',
        interventionTitle: 'Tavsiya etilgan aralashuv',
      },
      riskBadge: {
        title: 'Xavf balli: {score} — {band}',
        noData: "Ma'lumot yo'q",
      },
      recommendations: {
        sectionTitle: 'Tavsiyalar',
        addButton: "Tavsiya qo'shish",
        empty: "Faol tavsiyalar yo'q.",
        kebabAria: "Qo'shimcha amallar",
        suppressAction: 'Tavsiyani yashirish',
        sourceBadgeStarterPlan: "Boshlang'ich reja",
        mentorFrom: '{name} dan',
        targetTypeFallback: 'Maqsad',
        targetUnavailable: 'Bu kontent endi mavjud emas',
      },
      targetTypes: { module: 'Modul', lesson: 'Dars', quiz: 'Test' },
      states: {
        pending: 'Kutilmoqda', seen: "Ko'rildi", accepted: 'Qabul qilindi', dismissed: 'Rad etildi',
        completed: 'Yakunlandi', mentorSuppressed: 'Yashirilgan', superseded: 'Almashtirildi', expired: "Muddati o'tgan",
      },
      suppressed: {
        toggleShow: "Yashiringan va tarixni ko'rsatish ({count})",
        toggleHide: 'Yashiringan va tarixni yashirish ({count})',
      },
      interventionLog: { title: 'Aralashuvlar jurnali', empty: 'Hozircha aralashuvlar qayd etilmagan.' },
      suppressModal: {
        title: 'Ushbu tavsiyani yashirish',
        body: "Bu tavsiya o'quvchidan yashiriladi. Siz ko'rsatgan sabab AI kelajakda yaxshiroq takliflar yaratishiga yordam beradi.",
        presets: {
          prerequisiteNotMet: 'Oldingi shart bajarilmagan',
          notRelevantNow: 'Hozircha dolzarb emas',
          studentNotReady: "O'quvchi buning uchun hali tayyor emas",
          other: 'Boshqa',
        },
        otherPlaceholder: 'Ushbu tavsiya nima uchun mos emasligini tavsiflang',
        otherAria: 'Yashirish sababi',
        cancel: 'Bekor qilish',
        suppress: 'Yashirish',
        suppressing: 'Yashirilmoqda...',
      },
      addRecModal: {
        titleFor: "{name} uchun tavsiya qo'shish",
        targetTypeFieldLabel: 'Maqsad turi',
        targetFieldLabel: 'Maqsad',
        loadingContent: 'Kontent yuklanmoqda...',
        contentErrorNote: "O'quvchining kurslarini yuklab bo'lmadi. Oynani yopib, qayta urinib ko'ring.",
        noItemsAvailable: { module: "Mavjud modul yo'q", lesson: "Mavjud dars yo'q", quiz: "Mavjud test yo'q" },
        selectItem: { module: 'Modulni tanlang...', lesson: 'Darsni tanlang...', quiz: 'Testni tanlang...' },
        reasoningLabel: 'Asos',
        reasoningPlaceholder: "Bu tavsiya nima uchun ushbu o'quvchiga mos ekanligini tushuntiring.",
        priorityLabel: 'Ustuvorlik',
        skillsLabel: "Qamrab olingan ko'nikmalar",
        skillsSearchLoading: "Ko'nikmalar yuklanmoqda...",
        skillsSearchPlaceholder: "Ko'nikmalarni qidirish...",
        skillsSearchAria: "Ko'nikmalarni qidirish",
        noMatchingSkills: "Mos ko'nikma topilmadi",
        removeSkillAria: "{name} ko'nikmasini olib tashlash",
        cancel: 'Bekor qilish',
        add: "Tavsiya qo'shish",
        adding: "Qo'shilmoqda...",
      },
      retry: 'Qayta urinish',
      time: {
        today: 'Bugun', yesterday: 'Kecha',
        daysAgo: '{count} kun oldin',
        weekAgo: '1 hafta oldin', weeksAgo: '{count} hafta oldin',
        monthAgo: '1 oy oldin', monthsAgo: '{count} oy oldin',
      },
      toasts: {
        suppressed: 'Yashirildi',
        suppressError: "Tavsiyani yashirib bo'lmadi. Qaytadan urinib ko'ring.",
        recommendationAdded: "Tavsiya qo'shildi",
      },
      errors: {
        assessmentLoad: "Baholashni yuklab bo'lmadi. Qaytadan urinib ko'ring.",
        historyLoad: "Xavf tarixini yuklab bo'lmadi.",
        targetNotAvailable: "Bu maqsad ushbu o'quvchi uchun mavjud emas. Boshqa modul, dars yoki testni tanlang.",
        skillsUnavailable: "Tanlangan ko'nikmalardan biri yoki bir nechtasi mavjud emas. Sahifani yangilab, qaytadan urinib ko'ring.",
        networkError: "Ulanib bo'lmadi. Qaytadan urinib ko'ring.",
        addRecGeneric: "Tavsiyani qo'shib bo'lmadi. Kiritilgan ma'lumotlarni tekshirib, qaytadan urinib ko'ring.",
      },
      tabsAria: "O'quvchi bo'limlari",
      openProfile: "O'quvchi profilini ochish",
    },
  },
};

export default directory;
